import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  Button,
  useToast,
  Select,
  Container,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Input,
  InputGroup,
  InputLeftElement,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useBreakpointValue,
  FormControl,
  FormLabel,
  Link,
} from "@chakra-ui/react";
import { SearchIcon, ExternalLinkIcon, TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import { getAnunciosDenuncias } from "../services/api";
import Preloader from "../components/Preloader";
import ProdutoImagem from "../components/ProdutoImagem";
import ScrollToTop from "../components/ScrollToTop";

const PER_PAGE = 15;

const formatData = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  return isNaN(dt) ? "-" : dt.toLocaleDateString("pt-BR");
};

const STATUS_ML = {
  CREATED: "Denúncia criada",
  PENDING_MODERATION: "Em moderação",
  WAITING_DOCUMENTATION: "Aguardando resposta",
  ROLLBACK: "Revertida",
  DOCUMENTATION_PRESENTED: "Documentação apresentada",
  WAITING_FOR_PATCH_PDP: "Aguardando correção do catálogo",
  DOCUMENTATION_APPROVED: "Documentação aprovada",
  DOCUMENTATION_NOT_APPROVED: "Documentação não aprovada",
  DOCUMENTATION_NOT_PRESENTED: "Documentação não apresentada",
  MEMBER_NOT_RESPOND: "Denúncia sem resposta do membro",
  ACUERDO_CLOSED: "Restrição regulatória",
  DISCARDED: "Descartada",
  DISCARDED_DUE_RESTRICTION: "Descartada por restrição",
  REJECTED: "Rejeitada",
  REJECTED_BY_ABUSE: "Rejeitada por abuso",
  REJECTED_BY_ABUSE_COUNTER_NOTICE: "Rejeitada por abuso na contranotificação",
  REJECTED_BY_RESTRICTION: "Rejeitada por restrição",
  REJECTED_PDP_ITEM: "Rejeitada (item)",
  REJECTED_PDP_CATALOG: "Rejeitada (catálogo)",
  REJECTED_PDP_IMAGE_RANKER: "Rejeitada (classificador de imagens)",
  REJECTED_MODIFIED: "Rejeitada por modificação",
};

const SITUACOES = {
  responder: { label: "Precisa responder", color: "red", ordem: 0 },
  aguardando: { label: "Respondida", color: "orange", ordem: 1 },
  analise: { label: "Em análise do ML", color: "gray", ordem: 2 },
  favor: { label: "A nosso favor", color: "green", ordem: 3 },
  contra: { label: "Contra nós", color: "purple", ordem: 3 },
};

const statusLabel = (status) => STATUS_ML[status] || status || "-";

const COR_CONTA = { 1: "blue", 2: "purple", 3: "green" };

const RESPONDIDA = ["DOCUMENTATION_PRESENTED", "DOCUMENTATION_APPROVED", "DOCUMENTATION_NOT_APPROVED", "MEMBER_NOT_RESPOND"];

const nossaResposta = (d) => {
  const s = d.current_status || "";
  if (RESPONDIDA.includes(s)) return { label: "Sim", color: "green" };
  if (s === "DOCUMENTATION_NOT_PRESENTED") return { label: "Não", color: "red" };
  if (s === "WAITING_DOCUMENTATION") return { label: "Ainda não", color: "orange" };
  if (s === "ROLLBACK") return prazoAberto(d) ? { label: "Ainda não", color: "orange" } : { label: "Não", color: "red" };
  if (s === "WAITING_FOR_PATCH_PDP") return { label: "Corrigir anúncio", color: "orange" };
  if (STATUS_ML[s]) return { label: "Não exigida", color: "gray" };
  return { label: "Verificar", color: "gray" };
};

const prazoAberto = (d) => !!d.due_date && new Date(d.due_date) > new Date();

const situacao = (d) => {
  const s = d.current_status || "";
  if (s === "WAITING_DOCUMENTATION" || s === "WAITING_FOR_PATCH_PDP") return "responder";
  if (s === "ROLLBACK") return prazoAberto(d) ? "responder" : "contra";
  if (s === "DOCUMENTATION_PRESENTED") return "aguardando";
  if (s === "DOCUMENTATION_APPROVED" || s === "MEMBER_NOT_RESPOND" || s.startsWith("DISCARDED") || s.startsWith("REJECTED")) return "favor";
  if (s === "DOCUMENTATION_NOT_APPROVED" || s === "DOCUMENTATION_NOT_PRESENTED" || s === "ACUERDO_CLOSED") return "contra";
  return "analise";
};

const AnunciosDenuncias = () => {
  const toast = useToast();

  const [denuncias, setDenuncias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ field: "date_created", dir: "desc" });

  const [filtros, setFiltros] = useState({
    busca: "",
    conta: "todas",
    status: "todos",
    situacao: "todas",
  });

  const isMobile = useBreakpointValue({ base: true, lg: false });

  const fetchDenuncias = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAnunciosDenuncias();
      setDenuncias(Array.isArray(res) ? res : []);
    } catch (error) {
      toast({ title: "Erro ao carregar denúncias", status: "error", duration: 3000, isClosable: true });
      setDenuncias([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchDenuncias();
  }, [fetchDenuncias]);

  const statusOptions = useMemo(
    () => [...new Set(denuncias.map((d) => d.current_status).filter(Boolean))].sort(),
    [denuncias]
  );

  const stats = useMemo(() => {
    const contagem = { responder: 0, aguardando: 0, analise: 0, favor: 0, contra: 0 };
    denuncias.forEach((d) => { contagem[situacao(d)] += 1; });
    return contagem;
  }, [denuncias]);

  const filtradas = useMemo(() => {
    const busca = filtros.busca.trim().toLowerCase();
    let list = denuncias.filter((d) => {
      if (filtros.conta !== "todas" && String(d.conta) !== filtros.conta) return false;
      if (filtros.status !== "todos" && d.current_status !== filtros.status) return false;
      if (filtros.situacao !== "todas" && situacao(d) !== filtros.situacao) return false;
      if (busca) {
        const alvo = `${d.item_id || ""} ${d.case_id || ""} ${d.reason_text || ""} ${d.produto_nome || ""} ${d.produto_codigo_global || ""} ${d.produto_id || ""}`.toLowerCase();
        if (!alvo.includes(busca)) return false;
      }
      return true;
    });

    const { field, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const ra = SITUACOES[situacao(a)].ordem;
      const rb = SITUACOES[situacao(b)].ordem;
      if (ra !== rb) return ra - rb;

      let va = a[field];
      let vb = b[field];
      if (field === "date_created" || field === "due_date") {
        va = new Date(va).getTime() || 0;
        vb = new Date(vb).getTime() || 0;
      } else {
        va = (va ?? "").toString().toLowerCase();
        vb = (vb ?? "").toString().toLowerCase();
      }
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
    return list;
  }, [denuncias, filtros, sort]);

  const totalPages = Math.max(1, Math.ceil(filtradas.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtradas.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  useEffect(() => {
    setPage(1);
  }, [filtros, sort]);

  const handleFilterChange = (field, value) => setFiltros((prev) => ({ ...prev, [field]: value }));

  const handleSort = (field) =>
    setSort((prev) => (prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" }));

  const linkAnuncio = (itemId) => {
    if (!itemId) return undefined;
    const id = String(itemId).replace("MLB", "");
    return `https://produto.mercadolivre.com.br/MLB-${id}`;
  };

  const SortHeader = ({ field, children }) => (
    <Th cursor="pointer" userSelect="none" onClick={() => handleSort(field)} _hover={{ color: "orange.500" }}>
      <Flex align="center" gap={1}>
        {children}
        {sort.field === field && (sort.dir === "asc" ? <TriangleUpIcon boxSize={3} /> : <TriangleDownIcon boxSize={3} />)}
      </Flex>
    </Th>
  );

  const ProdutoInfo = ({ denuncia: d, compact }) => (
    <Flex align="center" gap={3} minW={0}>
      <Box flexShrink={0}>
        <ProdutoImagem
          produto={{ id: d.produto_id, id_ml: d.item_id, nome: d.produto_nome }}
          origem="Mercado Livre"
          width={compact ? "48px" : "44px"}
          height={compact ? "48px" : "44px"}
        />
      </Box>
      <Box minW={0}>
        <Link href={linkAnuncio(d.item_id)} isExternal color="orange.500" fontWeight="semibold" fontSize="sm">
          {d.item_id} <ExternalLinkIcon boxSize="11px" mb="2px" />
        </Link>
        {d.produto_nome && (
          <Text fontSize="xs" color="gray.700" noOfLines={1} maxW={compact ? "100%" : "240px"}>{d.produto_nome}</Text>
        )}
        {d.produto_id ? (
          <Link href={`${window.location.origin}/produtos?id=${d.produto_id}`} isExternal fontSize="xs" color="blue.500">
            SKU {d.produto_id}{d.produto_codigo_global ? ` · ${d.produto_codigo_global}` : ""} <ExternalLinkIcon boxSize="10px" mb="2px" />
          </Link>
        ) : (
          <Text fontSize="xs" color="gray.400">Produto não localizado</Text>
        )}
      </Box>
    </Flex>
  );

  const SituacaoBadge = ({ denuncia }) => {
    const sit = SITUACOES[situacao(denuncia)];
    return (
      <Badge colorScheme={sit.color} px={2} borderRadius="full" whiteSpace="nowrap">{sit.label}</Badge>
    );
  };

  const RespostaBadge = ({ denuncia }) => {
    const r = nossaResposta(denuncia);
    return <Badge colorScheme={r.color} variant="subtle" whiteSpace="nowrap">{r.label}</Badge>;
  };

  const StatusBadge = ({ status }) => (
    <Badge colorScheme="gray" variant="subtle" title={status} whiteSpace="normal" textTransform="none">{statusLabel(status)}</Badge>
  );

  const renderTable = () => (
    <Box overflowX="auto" borderRadius="lg" border="1px solid" borderColor="gray.200">
      <Table variant="simple" bg="white">
        <Thead bg="gray.50">
          <Tr>
            <Th>Conta</Th>
            <Th>Anúncio</Th>
            <SortHeader field="case_id">Caso</SortHeader>
            <Th px={3}>Respondemos?</Th>
            <SortHeader field="current_status">Status</SortHeader>
            <SortHeader field="reason_text">Motivo</SortHeader>
            <SortHeader field="date_created">Criação</SortHeader>
            <SortHeader field="due_date">Limite</SortHeader>
            <Th>Situação</Th>
          </Tr>
        </Thead>
        <Tbody>
          {pageItems.map((d) => (
            <Tr key={`${d.case_id}-${d.item_id}`} _hover={{ bg: "gray.50" }}>
              <Td><Badge colorScheme={COR_CONTA[d.conta] || "purple"} variant="subtle">Conta {d.conta}</Badge></Td>
              <Td><ProdutoInfo denuncia={d} /></Td>
              <Td fontSize="sm" color="gray.600" px={3}>{d.case_id}</Td>
              <Td px={3}><RespostaBadge denuncia={d} /></Td>
              <Td><StatusBadge status={d.current_status} /></Td>
              <Td fontSize="sm" maxW="280px" whiteSpace="normal"><Text noOfLines={3} title={d.reason_text}>{d.reason_text}</Text></Td>
              <Td fontSize="sm" color="gray.600">{formatData(d.date_created)}</Td>
              <Td fontSize="sm" color="gray.600">{formatData(d.due_date)}</Td>
              <Td><SituacaoBadge denuncia={d} /></Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );

  const renderCards = () => (
    <SimpleGrid columns={1} spacing={4}>
      {pageItems.map((d) => (
        <Card key={`${d.case_id}-${d.item_id}`} shadow="sm" border="1px solid" borderColor="gray.200">
          <CardHeader pb={2}>
            <Flex justify="space-between" align="flex-start" gap={2} wrap="wrap">
              <Box flex="1 1 180px" minW={0}>
                <ProdutoInfo denuncia={d} compact />
              </Box>
              <SituacaoBadge denuncia={d} />
            </Flex>
          </CardHeader>
          <CardBody py={2}>
            <Stack spacing={2} fontSize="sm">
              <Flex justify="space-between" gap={2}>
                <Text color="gray.500">Conta:</Text>
                <Badge colorScheme={COR_CONTA[d.conta] || "purple"} variant="subtle">Conta {d.conta}</Badge>
              </Flex>
              <Flex justify="space-between" gap={2}>
                <Text color="gray.500">Respondemos?</Text>
                <RespostaBadge denuncia={d} />
              </Flex>
              <Flex justify="space-between" gap={2}>
                <Text color="gray.500">Status:</Text>
                <StatusBadge status={d.current_status} />
              </Flex>
              <Flex justify="space-between" gap={2}>
                <Text color="gray.500">Caso:</Text>
                <Text>{d.case_id}</Text>
              </Flex>
              <Box>
                <Text color="gray.500">Motivo:</Text>
                <Text fontWeight="medium">{d.reason_text}</Text>
              </Box>
              <Flex justify="space-between" gap={2}>
                <Text color="gray.500">Criação:</Text>
                <Text>{formatData(d.date_created)}</Text>
              </Flex>
              <Flex justify="space-between" gap={2}>
                <Text color="gray.500">Limite:</Text>
                <Text>{formatData(d.due_date)}</Text>
              </Flex>
            </Stack>
          </CardBody>
        </Card>
      ))}
    </SimpleGrid>
  );

  return (
    <Box bg="gray.50" minH="100vh" py={6}>
      <Container maxW="container.xl">

        <Flex justify="space-between" align="center" mb={6} direction={{ base: "column", md: "row" }} gap={4}>
          <Heading size="lg" color="orange.500">Denúncias nos Anúncios</Heading>
          <Button colorScheme="orange" variant="outline" onClick={fetchDenuncias} isLoading={loading} w={{ base: "100%", md: "auto" }}>
            Atualizar
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={6}>
          <Card p={4} variant="outline" bg="white" borderLeft="4px solid" borderColor="red.400">
            <Stat><StatLabel color="gray.500">Precisa Responder</StatLabel><StatNumber color="red.500">{stats.responder}</StatNumber><StatHelpText>O robô responde de hora em hora</StatHelpText></Stat>
          </Card>
          <Card p={4} variant="outline" bg="white" borderLeft="4px solid" borderColor="orange.400">
            <Stat><StatLabel color="gray.500">Aguardando a Marca</StatLabel><StatNumber color="orange.500">{stats.aguardando}</StatNumber><StatHelpText>Já respondidas</StatHelpText></Stat>
          </Card>
          <Card p={4} variant="outline" bg="white" borderLeft="4px solid" borderColor="green.400">
            <Stat><StatLabel color="gray.500">A Nosso Favor</StatLabel><StatNumber color="green.500">{stats.favor}</StatNumber><StatHelpText>Anúncio mantido</StatHelpText></Stat>
          </Card>
          <Card p={4} variant="outline" bg="white" borderLeft="4px solid" borderColor="purple.400">
            <Stat><StatLabel color="gray.500">Contra Nós</StatLabel><StatNumber color="purple.500">{stats.contra}</StatNumber><StatHelpText>Anúncio removido</StatHelpText></Stat>
          </Card>
        </SimpleGrid>

        <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} alignItems="end">
            <FormControl>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>Busca</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none"><SearchIcon color="gray.300" /></InputLeftElement>
                <Input placeholder="Anúncio, SKU, produto, caso ou motivo..." value={filtros.busca} onChange={(e) => handleFilterChange("busca", e.target.value)} bg="gray.50" />
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>Conta</FormLabel>
              <Select value={filtros.conta} onChange={(e) => handleFilterChange("conta", e.target.value)} bg="gray.50">
                <option value="todas">Todas as Contas</option>
                <option value="1">Conta 1</option>
                <option value="2">Conta 2</option>
                <option value="3">Conta 3</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>Status</FormLabel>
              <Select value={filtros.status} onChange={(e) => handleFilterChange("status", e.target.value)} bg="gray.50">
                <option value="todos">Todos os Status</option>
                {statusOptions.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>Situação</FormLabel>
              <Select value={filtros.situacao} onChange={(e) => handleFilterChange("situacao", e.target.value)} bg="gray.50">
                <option value="todas">Todas</option>
                {Object.entries(SITUACOES).map(([valor, sit]) => <option key={valor} value={valor}>{sit.label}</option>)}
              </Select>
            </FormControl>
          </SimpleGrid>
        </Box>

        {loading ? (
          <Flex justify="center" py={20}><Preloader /></Flex>
        ) : (
          <>
            {filtradas.length === 0 ? (
              <Flex bg="white" p={10} justify="center" align="center" borderRadius="lg" border="1px dashed" borderColor="gray.300" direction="column">
                <Text fontSize="lg" color="gray.500" mb={4}>
                  {denuncias.length === 0 ? "Nenhuma denúncia encontrada." : "Nenhuma denúncia com os filtros atuais."}
                </Text>
                {denuncias.length > 0 && (
                  <Button variant="outline" onClick={() => setFiltros({ busca: "", conta: "todas", status: "todos", situacao: "todas" })}>
                    Limpar Filtros
                  </Button>
                )}
              </Flex>
            ) : (
              isMobile ? renderCards() : renderTable()
            )}

            {filtradas.length > PER_PAGE && (
              <Flex justify="space-between" align="center" mt={6}>
                <Button isDisabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} size="sm" variant="outline">Anterior</Button>
                <Text fontSize="sm" color="gray.600">
                  Página <b>{currentPage}</b> de <b>{totalPages}</b> ({filtradas.length} denúncias)
                </Text>
                <Button isDisabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} size="sm" variant="outline">Próxima</Button>
              </Flex>
            )}
          </>
        )}

      </Container>
      <ScrollToTop />
    </Box>
  );
};

export default AnunciosDenuncias;
